import * as daterangepicker from 'daterangepicker';
import jQuery from 'jquery';
import moment from 'moment';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';

import { addModifiers } from '../../shared/helpers';
import { formatMessageHelper } from '../../shared/helpers/internationalization';

import './ReactDaterangepicker.scss';

interface ReactDaterangepickerProps {
  showDropdowns?: boolean;
  minYear?: number; // The minimum year shown in the dropdowns
  maxYear?: number; // The maximum year shown in the dropdowns
  singleDatePicker?: boolean; // Show only a single calendar
  ranges?: boolean; // Set predefined date ranges the user can select from
  rangesType?: 'default' | 'dataExplorer'; // Which set of predefined ranges to show
  startDate?: moment.Moment | string; // The beginning date of the initially selected date range.
  endDate?: moment.Moment | string; // The end date of the initially selected date range
  opens?: string; // The picker alignement
  maxDate?: moment.Moment; // The latest date a user may select.
  autoUpdateInput?: boolean; // Add to the input current date if the startDate and endDate are set;
  linkedCalendars?: boolean; // The two calendars displayed will always be for two sequential months
  handleChange: (dates: moment.Moment[] | string[]) => void;
  // A handle that allows some operations with the startDate endDate valaus and retun its with diferent formats
  handleDatesBeforeChange?: (start: moment.Moment, end: moment.Moment) => moment.Moment[] | string[];
  modifiers?: string[];
  placeholder?: string;
  initDefaultDate?: boolean; // If the datepicker has a default date  when it started
}

class ReactDaterangepicker extends React.Component<ReactDaterangepickerProps & InjectedIntlProps> {
  private $el: any;
  private el: any;

  componentDidMount() {
    this.$el = jQuery(this.el);

    const { initDefaultDate = true } = this.props;
    const handleChange = !initDefaultDate ? this.handleChange : () => '';
    this.$el.daterangepicker(this.setOption(), handleChange);
  }

  componentDidUpdate(prevProps: ReactDaterangepickerProps) {
    const startDate: moment.Moment | undefined | string = this.props.startDate;
    const endDate: moment.Moment | undefined | string = this.props.endDate;
    if (prevProps.startDate && prevProps.endDate && !startDate && !endDate) {
      // Reset the widget's internal range too, not just the input text — otherwise re-opening
      // the picker and clicking Apply would re-emit the cleared (stale) range. Blank the input
      // last, since setStartDate/setEndDate write the default range back into it.
      this.$el.data('daterangepicker').setStartDate(this.defaultDate().startDate);
      this.$el.data('daterangepicker').setEndDate(this.defaultDate().endDate);
      this.$el.val('');
    } else if (!startDate && !endDate && prevProps.startDate !== startDate && prevProps.endDate !== endDate) {
      this.$el.val('');
      this.$el.data('daterangepicker').setStartDate(this.defaultDate().startDate);
      this.$el.data('daterangepicker').setEndDate(this.defaultDate().endDate);
    } else if (startDate && endDate) {
      this.$el.val(`${moment(startDate).format('L')} - ${moment(endDate).format('L')}`);
      this.$el.data('daterangepicker').setStartDate(moment(startDate));
      this.$el.data('daterangepicker').setEndDate(moment(endDate));
    }
  }

  componentWillUnmount() {
    this.$el.data('daterangepicker').remove();
  }

  setRef = (el: any) => (this.el = el);

  setranges() {
    if (!this.props.ranges) {
      return {};
    }

    const { intl } = this.props;

    if (this.props.rangesType === 'dataExplorer') {
      return {
        ranges: {
          [formatMessageHelper(intl, 'daterangepicker.last.month')]: [moment().subtract(1, 'month'), moment()],
          [formatMessageHelper(intl, 'daterangepicker.last3Months')]: [moment().subtract(3, 'months'), moment()],
          [formatMessageHelper(intl, 'daterangepicker.last6Months')]: [moment().subtract(6, 'months'), moment()],
          [formatMessageHelper(intl, 'daterangepicker.lastYear')]: [moment().subtract(1, 'year'), moment()],
        },
      };
    }

    return {
      ranges: {
        [formatMessageHelper(intl, 'daterangepicker.today')]: [moment(), moment()],
        [formatMessageHelper(intl, 'daterangepicker.yesterday')]: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
        [formatMessageHelper(intl, 'daterangepicker.last7d')]: [moment().subtract(6, 'days'), moment()],
        [formatMessageHelper(intl, 'daterangepicker.last30d')]: [moment().subtract(29, 'days'), moment()],
        [formatMessageHelper(intl, 'daterangepicker.month')]: [moment().startOf('month'), moment()],
        [formatMessageHelper(intl, 'daterangepicker.last.month')]: [
          moment().subtract(1, 'month').startOf('month'),
          moment().subtract(1, 'month').endOf('month'),
        ],
      },
    };
  }

  handleChange = (start: moment.Moment, end: moment.Moment) => {
    if (this.props.handleDatesBeforeChange) {
      this.props.handleChange(this.props.handleDatesBeforeChange(start, end));
    } else {
      this.props.handleChange([start, end]);
    }
  };

  setAutoUpdateInput() {
    const { initDefaultDate = true } = this.props;
    if (!this.props.startDate && !this.props.endDate) {
      this.$el.on('apply.daterangepicker', (_: any, picker: { startDate: moment.Moment; endDate: moment.Moment }) => {
        if (initDefaultDate) {
          this.handleChange(picker.startDate, picker.endDate);
        }

        this.$el.val(`${picker.startDate.format('L')} - ${picker.endDate.format('L')}`);
      });

      return {
        autoUpdateInput: false,
      };
    } else {
      if (initDefaultDate) {
        this.$el.on('apply.daterangepicker', (_: any, picker: { startDate: moment.Moment; endDate: moment.Moment }) => {
          this.handleChange(picker.startDate, picker.endDate);
        });
      }

      return {
        autoUpdateInput: true,
      };
    }
  }

  defaultDate(): { startDate: moment.Moment; endDate: moment.Moment } {
    return {
      startDate: moment().subtract(1, 'month'),
      endDate: moment(),
    };
  }

  setOption() {
    const { initDefaultDate = true, opens = 'right', singleDatePicker = false, showDropdowns = false } = this.props;

    const defaultStartDate = initDefaultDate ? { startDate: this.defaultDate().startDate } : {};
    const defaultEndDate = initDefaultDate ? { endDate: this.defaultDate().endDate } : {};

    const minYear = this.props.minYear ? { minYear: this.props.minYear } : {};
    const maxYear = this.props.maxYear ? { maxYear: this.props.maxYear } : {};
    const startDate = this.props.startDate ? { startDate: moment(this.props.startDate) } : defaultStartDate;
    const endDate = this.props.endDate ? { endDate: moment(this.props.endDate) } : defaultEndDate;

    return {
      ...minYear,
      ...maxYear,
      maxDate: moment().endOf('month').format('L'),
      ...this.setranges(),
      ...startDate,
      ...endDate,
      alwaysShowCalendars: true,
      showCustomRangeLabel: true,
      singleDatePicker,
      opens,
      ...this.setAutoUpdateInput(),
      showDropdowns,
      linkedCalendars: false,
      buttonClasses: 'button',
      applyButtonClasses: 'button--primary',
      cancelClass: 'button--secondary',
      locale: {
        cancelLabel: formatMessageHelper(this.props.intl, 'daterangepicker.cancel'),
        applyLabel: formatMessageHelper(this.props.intl, 'daterangepicker.apply'),
        customRangeLabel: formatMessageHelper(this.props.intl, 'daterangepicker.custom.range'),
        format: 'L',
      },
    };
  }

  render() {
    const { placeholder = '', modifiers = [] } = this.props;

    return (
      <div className={addModifiers('form__input', modifiers)}>
        <input ref={this.setRef} placeholder={placeholder} autoComplete="false" />
      </div>
    );
  }
}
export const dateRangePicker = daterangepicker;

export default injectIntl(ReactDaterangepicker);
